/**
 * Helper methods for toasts and error parsing.
 *
 * Toasts are rendered via `toast.custom` with a card-style component aligned
 * on the design system tokens (--card, --foreground, --border) so they look
 * good in light AND dark mode. Each variant has a colored left bar + icon.
 */
import toast from 'react-hot-toast';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  X as XIcon,
} from 'lucide-react';

/**
 * Parse an axios error response into either a per-field map ({ field: msg })
 * or a flat string. Used by forms to surface server-side errors.
 */
export const getError = (payload) => {
  const data = payload.response.data;
  const regex = /\"data.([a-zA-Z]+)\" (.+)/;
  const result = data?.message?.match(regex);
  if (result) {
    return { [result[1]]: result[2] };
  } else if (data.message) {
    return data.message;
  }
  return false;
};

export const toastError = (payload) => {
  const data = payload.response.data;
  const regex = /\"data.([a-zA-Z]+)\" (.+)/;
  const result = data?.message?.match(regex);
  if (result) {
    return result[1] + ' field ' + result[2];
  } else if (data.message) {
    return data.message;
  }
  return false;
};

const VARIANT_STYLES = {
  success: {
    Icon: CheckCircle2,
    iconClass: 'text-emerald-600 dark:text-emerald-400',
    barClass: 'bg-emerald-500',
  },
  error: {
    Icon: XCircle,
    iconClass: 'text-red-500 dark:text-red-400',
    barClass: 'bg-red-500',
  },
  warning: {
    Icon: AlertTriangle,
    iconClass: 'text-amber-500 dark:text-amber-400',
    barClass: 'bg-amber-500',
  },
  info: {
    Icon: Info,
    iconClass: 'text-sky-500 dark:text-sky-400',
    barClass: 'bg-sky-500',
  },
};

function ToastCard({ t, variant, message }) {
  const v = VARIANT_STYLES[variant] ?? VARIANT_STYLES.info;
  const Icon = v.Icon;
  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={`pointer-events-auto relative flex w-full max-w-sm items-start gap-3 overflow-hidden rounded-lg border border-border bg-card py-3 pl-4 pr-2 text-sm font-medium text-foreground shadow-lg shadow-foreground/[0.06] transition-all ${
        t.visible
          ? 'animate-in fade-in slide-in-from-top-2'
          : 'animate-out fade-out slide-out-to-right-2'
      }`}
    >
      <span
        aria-hidden="true"
        className={`absolute left-0 top-0 h-full w-[3px] ${v.barClass}`}
      />
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${v.iconClass}`} />
      <div className="flex-1 leading-snug">{message}</div>
      <button
        type="button"
        onClick={() => toast.dismiss(t.id)}
        className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label="Close"
      >
        <XIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

const FALLBACK_MESSAGES = {
  success: 'Done.',
  error: 'Something went wrong.',
  warning: 'Please review the changes.',
  info: 'Information',
};

const normalizeMessage = (variant, message) => {
  if (message == null) return FALLBACK_MESSAGES[variant];
  if (typeof message === 'string') {
    const trimmed = message.trim();
    return trimmed.length > 0 ? trimmed : FALLBACK_MESSAGES[variant];
  }
  // React nodes (icons, JSX) pass through unchanged.
  return message;
};

const showToast = (variant, message, opts = {}) => {
  const safeMessage = normalizeMessage(variant, message);
  return toast.custom(
    (t) => <ToastCard t={t} variant={variant} message={safeMessage} />,
    {
      // Toasts disappear quickly — they're confirmations, not blockers.
      // Errors stay a touch longer so they can be read.
      duration: variant === 'error' ? 3500 : 2200,
      position: 'top-right',
      ...opts,
    },
  );
};

export const successMessageToast = (message) => showToast('success', message);
export const errorMessageToast = (message) => showToast('error', message);
export const warningMessageToast = (message) => showToast('warning', message);
export const infoMessageToast = (message) => showToast('info', message);
